import { useEffect, useState, useRef } from "react";
import {
  Box,
  Stack,
  List,
  Badge,
  IconButton,
  Typography,
  Divider,
  Modal,
  ListItemButton,
  ListItemText,
  Button,
} from "@mui/material";
import { fToNow } from "../../../utils/formatTime";
import Iconify from "../../../components/iconify";
import Scrollbar from "../../../components/scrollbar";
import MenuPopover from "../../../components/menu-popover";
import { IconButtonAnimate } from "../../../components/animate";
import useService, { QueryKeys } from "src/hooks/useService";
import { useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import { useNavigate } from "react-router";
import { PATH_DASHBOARD } from "src/routes/paths";

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const selectorAuth = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const [openPopover, setOpenPopover] = useState<HTMLElement | null>(null);

  const [notifications, setNotifications] = useState<Array<any>>([]);
  const [totalUnRead, setTotalUnRead] = useState(
    notifications.filter((item) => item.status !== "read").length
  );

  const [openModal, setOpenModal] = useState(false);
  const [latestNotification, setLatestNotification] = useState<any | null>(
    null
  );

  const lastNotificationId = useRef<string | null>(null);

  const { data: notificationsData, refetch: triggerNotification } = useService(
    `/notification/getByOwner`,
    {
      manual: true,
      key: QueryKeys.NOTIFICATION_HOME,
      cacheMinutes: 0,
    }
  );

  const { put: triggerNotificationsPut } = useService(
    `/notification/updateRead/${
      selectorAuth.userReturn?.isSubUser
        ? selectorAuth.userReturn?.ownerId
        : selectorAuth.userReturn.id
    }`,
    {
      manual: true,
      key: QueryKeys.NOTIFICATION_HOME,
      cacheMinutes: 0,
    }
  );

  useEffect(() => {
    triggerNotification();

    const interval = setInterval(() => {
      triggerNotification();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (notificationsData) {
      let arr: Array<any> = [...notificationsData].reverse();
      setNotifications(arr);

      // detectar se chegou uma nova notificação
      if (arr.length > 0) {
        const newest = arr[0];
        if (newest._id !== lastNotificationId.current) {
          // nova notificação encontrada
          if (lastNotificationId.current !== null && newest.status !== "read") {
            if (newest?.schedulingId || newest?.clientId || newest?.orderId) {
              setLatestNotification(newest);
              setOpenModal(true);
            }
          }
          lastNotificationId.current = newest._id;
        }
        setTotalUnRead(arr.filter((item) => item.status !== "read").length);
      }
    }
  }, [notificationsData]);

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setOpenPopover(event.currentTarget);
    triggerNotificationsPut({});
    triggerNotification();
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  // ✅ Conteúdo: Notificação de agendamento
  const NotificationContent = ({
    latestNotification,
    onClose,
    navigate,
  }: any) => (
    <>
      <Box
        component="img"
        src="/logo/logo_single.svg"
        sx={{ width: 35, mb: 2 }}
      />

      <Typography variant="h4" sx={{ mb: 2, textAlign: "center" }}>
        {latestNotification.title}
      </Typography>

      <Box
        component="img"
        src="/assets/images/home/scheduling-notification.svg"
        sx={{ width: 290, mb: 2 }}
      />

      <Typography variant="body1" sx={{ textAlign: "center", width: 350 }}>
        {latestNotification.description}
      </Typography>

      <Divider sx={{ borderStyle: "dashed", width: 350, my: 2 }} />

      <Button
        onClick={() => {
          onClose();
          window.location.href = `${PATH_DASHBOARD.general.schedule}?schedule=${latestNotification.schedulingId}`;
        }}
        variant="contained"
        sx={{ width: 350 }}
      >
        Verificar
      </Button>
    </>
  );

  // ✅ Conteúdo: Notificação de delivery
  const DeliveryNotificationContent = ({
    latestNotification,
    onClose,
    navigate,
  }: any) => (
    <>
      <Box
        component="img"
        src="/logo/logo_single.svg"
        sx={{ width: 35, mb: 2 }}
      />

      <Typography variant="h4" sx={{ mb: 2, textAlign: "center" }}>
        {latestNotification.title}
      </Typography>

      <Box
        component="img"
        src="/assets/images/home/delivery-notification.svg"
        sx={{ width: 290, mb: 2 }}
      />

      <Typography variant="body1" sx={{ textAlign: "center", width: 350 }}>
        {latestNotification.description}
      </Typography>

      <Divider sx={{ borderStyle: "dashed", width: 350, my: 2 }} />

      <Button
        onClick={() => {
          onClose();
          window.location.href = `${PATH_DASHBOARD.delivery.orders}?order=${latestNotification.orderId}`;
        }}
        variant="contained"
        sx={{ width: 350 }}
      >
        Verificar
      </Button>
    </>
  );

  // ✅ Conteúdo: Follow-up individual cancelado
  const FollowUpCanceledContent = ({
    latestNotification,
    onClose,
  }: any) => (
    <>
      <Box
        component="img"
        src="/logo/logo_single.svg"
        sx={{ width: 35, mb: 2 }}
      />

      <Typography variant="h4" sx={{ mb: 2, textAlign: "center" }}>
        {latestNotification.title || "Follow-up cancelado"}
      </Typography>

      <Typography
        variant="body1"
        sx={{ textAlign: "center", width: 350, mb: 2 }}
      >
        {latestNotification.description}
      </Typography>

      <Button
        onClick={() => {
          navigate(
            `${PATH_DASHBOARD.general.chats}?chat=${
              latestNotification?.clientId || ""
            }`
          );
          onClose();
        }}
        variant="contained"
        sx={{ width: 350 }}
      >
        Acessar Conversa
      </Button>
    </>
  );

  // ✅ Conteúdo: Atendimento humano solicitado
  const HumanSupportContent = ({
    latestNotification,
    onClose,
    navigate,
  }: any) => (
    <>
      <Box
        component="img"
        src="/logo/logo_single.svg"
        sx={{ width: 35, mb: 2 }}
      />

      <Typography variant="h4" sx={{ mb: 2, textAlign: "center" }}>
        Atendimento Humano Solicitado
      </Typography>

      <Box
        component="img"
        src="/assets/images/home/alfred.png"
        sx={{ width: 200, mb: 2 }}
      />

      <Typography
        variant="body1"
        sx={{ textAlign: "center", width: 350, mb: 2 }}
      >
        {latestNotification.description}
      </Typography>

      <Button
        onClick={() => {
          navigate(
            `${PATH_DASHBOARD.general.chats}?chat=${
              latestNotification?.clientId || ""
            }`
          );
          onClose();
        }}
        variant="contained"
        sx={{ width: 350 }}
      >
        Acessar Conversa
      </Button>
    </>
  );

  return (
    <>
      <IconButtonAnimate
        color={openPopover ? "primary" : "default"}
        onClick={handleOpenPopover}
        sx={{ width: 40, height: 40 }}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify icon="eva:bell-fill" />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover
        open={openPopover}
        onClose={handleClosePopover}
        sx={{ width: 360, p: 0 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Notificações</Typography>
          </Box>
        </Box>

        <Divider sx={{ borderStyle: "dashed" }} />

        <Scrollbar sx={{ height: { xs: 340, sm: "auto" }, maxHeight: "500px" }}>
          <List disablePadding>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification._id}
                notification={notification}
                navigate={(path: string) => {
                  navigate(path);
                  handleClosePopover();
                }}
              />
            ))}
            {notifications.length === 0 && (
              <Typography variant="body2" py={2} textAlign={"center"}>
                Sem notificações
              </Typography>
            )}
          </List>
        </Scrollbar>
      </MenuPopover>

      {/* Modal de notificação nova */}
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            bgcolor: "common.black",
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Botão fechar */}
          <IconButton
            onClick={() => {
              setOpenModal(false);
              window.location.reload();
            }}
            sx={{ position: "absolute", top: 16, right: 16 }}
          >
            <Iconify icon="eva:close-fill" />
          </IconButton>

          {/* Renderiza o conteúdo desejado */}
          {latestNotification?.orderId ? (
            <DeliveryNotificationContent
              latestNotification={latestNotification}
              navigate={navigate}
              onClose={() => setOpenModal(false)}
            />
          ) : latestNotification?.schedulingId ? (
            <NotificationContent
              latestNotification={latestNotification}
              navigate={navigate}
              onClose={() => setOpenModal(false)}
            />
          ) : latestNotification?.type === "followup" ? (
            <FollowUpCanceledContent
              latestNotification={latestNotification}
              onClose={() => setOpenModal(false)}
            />
          ) : (
            <HumanSupportContent
              latestNotification={latestNotification}
              navigate={navigate}
              onClose={() => setOpenModal(false)}
            />
          )}
        </Box>
      </Modal>
    </>
  );
}

// ----------------------------------------------------------------------

function NotificationItem({
  notification,
  navigate,
}: {
  notification: any;
  navigate: any;
}) {
  const { title } = renderContent(notification);

  return (
    <ListItemButton
      sx={{
        py: 1.5,
        px: 2.5,
        mt: "1px",
        ...(notification.status === "new" && {
          bgcolor: "action.selected",
        }),
      }}
      onClick={() => {
        if (notification?.orderId) {
          navigate(
            `${PATH_DASHBOARD.delivery.orders}?order=${notification.orderId}`
          );
        } else if (notification?.schedulingId) {
          navigate(
            `${PATH_DASHBOARD.general.schedule}?schedule=${notification.schedulingId}`
          );
        } else if (notification?.clientId) {
          navigate(
            `${PATH_DASHBOARD.general.chats}?chat=${notification.clientId}`
          );
        } else if (notification?.campaignId) {
          navigate(PATH_DASHBOARD.campaign.details(notification.campaignId));
        }
      }}
    >
      <ListItemText
        disableTypography
        primary={title}
        secondary={
          <Stack
            direction="row"
            sx={{ mt: 0.5, typography: "caption", color: "text.disabled" }}
          >
            <Iconify icon="eva:clock-fill" width={16} sx={{ mr: 0.5 }} />
            <Typography variant="caption">
              {fToNow(notification.updatedAt)}
            </Typography>
          </Stack>
        }
      />
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function renderContent(notification: any) {
  const title = (
    <Typography variant="subtitle2">
      {notification.title}
      <Typography
        component="span"
        variant="body2"
        sx={{ color: "text.secondary" }}
      >
        &nbsp; {notification.description}
      </Typography>
    </Typography>
  );

  return {
    title,
  };
}
